/**
 * @fileoverview Modern Auth Service - wrapper around AuthApiClient
 * Maintains backward compatibility while using the new API client architecture
 */

import { authApi } from '../api/apiClientFactory';
import type { 
  LoginRequest,
  LoginResponse,
  SignupRequest,
  UserProfile
} from '../api/clients/AuthApiClient';

export class AuthService {
  /**
   * Login user
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    return authApi.login(credentials);
  }

  /**
   * Register new user
   */
  async signup(userData: SignupRequest): Promise<LoginResponse> {
    return authApi.signup(userData);
  }

  /**
   * Refresh authentication token
   */
  async refreshToken(): Promise<{ token: string }> {
    // Get refresh token from storage - simplified for wrapper compatibility
    const refreshToken = localStorage.getItem('refreshToken') || '';
    const response = await authApi.refreshToken(refreshToken);
    return { token: response.token };
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    return authApi.logout();
  }

  /**
   * Get current user profile
   */
  async getProfile(): Promise<UserProfile> {
    return authApi.getProfile();
  }

  /**
   * Change user password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    return authApi.changePassword({ currentPassword, newPassword });
  }

  /**
   * Send password reset email
   */
  async sendPasswordReset(email: string): Promise<void> {
    return authApi.forgotPassword({ email });
  }

  /**
   * Verify email address
   */
  async verifyEmail(token: string): Promise<void> {
    return authApi.verifyEmail({ token });
  }
}

// Export singleton instance
export const authService = new AuthService();
